import type { SessionQuote, SeatPaymentKind } from '../models/sessions/Session';

/**
 * The server's TaxCalculator.TaxFor, to the cent: `taxable <= 0 || rate <= 0 ? 0 :
 * round_half_away_from_zero(taxable * rate / 10000)`, which C# does in exact decimal arithmetic.
 *
 * Integer maths only. `Math.round(taxable * (rate / 10000))` drifts: 200 at 7.25% is 14.5 cents of tax,
 * which the server rounds to 15 and the float product rounds to 14. With positive integers,
 * floor((n + 5000) / 10000) is round-half-up of n / 10000, which for positive values IS half away from zero.
 */
export function taxFor(taxableMinor: number, rateBasisPoints: number): number {
  if (taxableMinor <= 0 || rateBasisPoints <= 0) return 0;
  return Math.floor((taxableMinor * rateBasisPoints + 5000) / 10000);
}

export interface SessionTotals {
  /** The chosen card seats' prices, before tax. */
  cardSubtotalCents: number;
  taxCents: number;
  /** What the card is charged: subtotal plus tax. The book call's expectedCardCents. */
  cardCents: number;
  /** The credits the chosen credit seats spend. The book call's expectedCredits. */
  credits: number;
}

/**
 * What the chosen payments cost, exactly as the server's ISessionQuoteService.Totals computes it: the
 * card seats' prices summed, plus TaxFor(that sum). The server splits that tax across the seats by largest
 * remainder, which always sums back to TaxFor(sum), so the total is all the book call needs.
 *
 * Null when a choice isn't one of its seat's options, or the guest choices don't match the quote's
 * guests: the server refuses those rather than pricing them, so there is no total to show.
 */
export function sessionTotals(
  quote: SessionQuote, memberPayment: SeatPaymentKind, guestPayments: SeatPaymentKind[],
): SessionTotals | null {
  if (guestPayments.length !== quote.guests.length) return null;
  const seats = [quote.member, ...quote.guests];
  const choices = [memberPayment, ...guestPayments];
  const chosen = seats.map((seat, i) => seat.options.find((o) => o.payment === choices[i]));
  if (chosen.some((o) => o === undefined)) return null;

  let cardSubtotalCents = 0;
  let credits = 0;
  for (const o of chosen) {
    if (!o) continue;
    if (o.payment === 'card') cardSubtotalCents += o.priceCents;
    credits += o.credits;
  }
  const taxCents = taxFor(cardSubtotalCents, quote.taxRateBasisPoints);
  return { cardSubtotalCents, taxCents, cardCents: cardSubtotalCents + taxCents, credits };
}
